import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface PlanetData {
  name: string;
  nameEn: string;
  orbitRadius: number;
  orbitSpeed: number;
  visualSize: number;
  color: string;
  hasRings?: boolean;
  diameter: number;
  distanceAU: number;
  orbitalDays: number;
  moons: number;
  avgTemp: string;
  mass: string;
  funFact: string;
}

// NASA Planetary Fact Sheet — real astronomical data
const planets: PlanetData[] = [
  {
    name: '水星', nameEn: 'Mercury',
    orbitRadius: 65, orbitSpeed: 0.48, visualSize: 8, color: '#B5B5B5',
    diameter: 4879, distanceAU: 0.39, orbitalDays: 88, moons: 0,
    avgTemp: '167°C', mass: '3.30×10²³ kg',
    funFact: '自转周期176地球日，一天比一年还长',
  },
  {
    name: '金星', nameEn: 'Venus',
    orbitRadius: 110, orbitSpeed: 0.35, visualSize: 15, color: '#FFC649',
    diameter: 12104, distanceAU: 0.72, orbitalDays: 225, moons: 0,
    avgTemp: '464°C', mass: '4.87×10²⁴ kg',
    funFact: '浓密大气导致温室效应，表面温度高达464°C',
  },
  {
    name: '地球', nameEn: 'Earth',
    orbitRadius: 140, orbitSpeed: 0.25, visualSize: 16, color: '#4A90E2',
    diameter: 12756, distanceAU: 1.00, orbitalDays: 365, moons: 1,
    avgTemp: '15°C', mass: '5.97×10²⁴ kg',
    funFact: '唯一已知拥有液态水和生命的行星',
  },
  {
    name: '火星', nameEn: 'Mars',
    orbitRadius: 175, orbitSpeed: 0.18, visualSize: 12, color: '#E07A5F',
    diameter: 6792, distanceAU: 1.52, orbitalDays: 687, moons: 2,
    avgTemp: '-65°C', mass: '6.42×10²³ kg',
    funFact: '奥林匹斯山高21.9km，太阳系最高峰',
  },
  {
    name: '木星', nameEn: 'Jupiter',
    orbitRadius: 225, orbitSpeed: 0.07, visualSize: 44, color: '#D4A574',
    diameter: 142984, distanceAU: 5.20, orbitalDays: 4333, moons: 95,
    avgTemp: '-110°C', mass: '1.90×10²⁷ kg',
    funFact: '大红斑风暴已持续350+年，可容纳3个地球',
  },
  {
    name: '土星', nameEn: 'Saturn',
    orbitRadius: 275, orbitSpeed: 0.045, visualSize: 38, color: '#E8D5A3',
    hasRings: true,
    diameter: 120536, distanceAU: 9.58, orbitalDays: 10759, moons: 146,
    avgTemp: '-140°C', mass: '5.68×10²⁶ kg',
    funFact: '密度仅0.687g/cm³，理论上能浮在水面上',
  },
  {
    name: '天王星', nameEn: 'Uranus',
    orbitRadius: 320, orbitSpeed: 0.028, visualSize: 26, color: '#7EC8E3',
    diameter: 51118, distanceAU: 19.22, orbitalDays: 30687, moons: 28,
    avgTemp: '-195°C', mass: '8.68×10²⁵ kg',
    funFact: '自转轴倾斜97.77°，几乎"躺着"绕太阳公转',
  },
  {
    name: '海王星', nameEn: 'Neptune',
    orbitRadius: 360, orbitSpeed: 0.018, visualSize: 24, color: '#4169E1',
    diameter: 49528, distanceAU: 30.05, orbitalDays: 60190, moons: 16,
    avgTemp: '-200°C', mass: '1.02×10²⁶ kg',
    funFact: '风速达2100km/h，太阳系风速最快的行星',
  },
];

const innerPlanets = planets.slice(0, 4);
const outerPlanets = planets.slice(4);

const CX = 430;
const CY = 560;
const SUN_R = 40;
const CARD_LEFT = 980;
const CARD_W = 880;

const fadeRange = (
  frame: number,
  inStart: number, inEnd: number,
  outStart: number, outEnd: number,
) => {
  const a = interpolate(frame, [inStart, inEnd], [0, 1], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });
  const b = interpolate(frame, [outStart, outEnd], [1, 0], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });
  return a * b;
};

// ===== Star Field =====
const StarField: React.FC<{ opacity: number }> = ({ opacity }) => {
  const frame = useCurrentFrame();
  const stars = React.useMemo(() => {
    const out: { x: number; y: number; s: number; b: number; twinkleSpeed: number }[] = [];
    for (let i = 0; i < 160; i++) {
      const h1 = ((i * 7919 + 104729) % 100000) / 100000;
      const h2 = ((i * 6271 + 73849) % 100000) / 100000;
      const h3 = ((i * 3571 + 85691) % 100000) / 100000;
      out.push({
        x: h1 * 1920,
        y: h2 * 1080,
        s: 1 + h3 * 1.8,
        b: 0.2 + h3 * 0.8,
        twinkleSpeed: 0.02 + h1 * 0.06,
      });
    }
    return out;
  }, []);

  return (
    <div style={{ position: 'absolute', inset: 0, opacity }}>
      {stars.map((st, i) => {
        const twinkle = 0.6 + 0.4 * Math.sin(frame * st.twinkleSpeed + i);
        return (
          <div key={i} style={{
            position: 'absolute', left: st.x, top: st.y,
            width: st.s, height: st.s, borderRadius: '50%',
            background: `rgba(255,255,255,${st.b * twinkle})`,
          }} />
        );
      })}
    </div>
  );
};

// ===== Sun =====
const Sun: React.FC<{ opacity: number }> = ({ opacity }) => {
  const frame = useCurrentFrame();
  const pulse = 1 + Math.sin(frame * 0.06) * 0.04;
  const d = SUN_R * 2;
  return (
    <div style={{
      position: 'absolute',
      width: d, height: d,
      left: CX - SUN_R, top: CY - SUN_R,
      transform: `scale(${pulse})`,
      background: 'radial-gradient(circle, #FFD700 0%, #FF8C00 60%, #FF4500 100%)',
      borderRadius: '50%',
      boxShadow: '0 0 50px rgba(255,215,0,0.7), 0 0 100px rgba(255,140,0,0.4), 0 0 180px rgba(255,69,0,0.2)',
      opacity,
    }} />
  );
};

// ===== Orbit Ring =====
const OrbitRing: React.FC<{ radius: number; opacity: number; bright?: boolean }> = ({
  radius, opacity, bright,
}) => (
  <div style={{
    position: 'absolute',
    width: radius * 2, height: radius * 2,
    left: CX - radius, top: CY - radius,
    border: `1px solid rgba(255,255,255,${bright ? 0.3 : 0.1})`,
    borderRadius: '50%',
    opacity,
  }} />
);

// ===== Asteroid Belt =====
const AsteroidBelt: React.FC<{ frame: number; opacity: number }> = ({ frame, opacity }) => {
  const rocks = React.useMemo(() => {
    const out: { angle: number; r: number; s: number; spd: number }[] = [];
    for (let i = 0; i < 55; i++) {
      const h1 = ((i * 4591 + 67829) % 100000) / 100000;
      const h2 = ((i * 8713 + 23159) % 100000) / 100000;
      out.push({
        angle: h1 * Math.PI * 2,
        r: 188 + h2 * 26,
        s: 1 + h1 * 1.5,
        spd: 0.002 + h2 * 0.004,
      });
    }
    return out;
  }, []);

  return (
    <>
      {rocks.map((r, i) => {
        const a = r.angle + frame * r.spd;
        return (
          <div key={i} style={{
            position: 'absolute',
            left: CX + Math.cos(a) * r.r,
            top: CY + Math.sin(a) * r.r,
            width: r.s, height: r.s, borderRadius: '50%',
            background: 'rgba(160,160,160,0.45)',
            opacity,
          }} />
        );
      })}
    </>
  );
};

// ===== Planet Visual =====
const PlanetBody: React.FC<{
  planet: PlanetData;
  frame: number;
  fps: number;
  opacity: number;
  bright?: boolean;
}> = ({ planet, frame, fps, opacity, bright }) => {
  const angle = (frame / fps) * planet.orbitSpeed * Math.PI * 2;
  const px = CX + Math.cos(angle) * planet.orbitRadius;
  const py = CY + Math.sin(angle) * planet.orbitRadius;
  const enter = spring({ frame, fps, config: { damping: 12, stiffness: 200 } });
  const sz = planet.visualSize * (bright ? 1.25 : 1);
  const glow = bright ? sz * 0.8 : sz * 0.4;

  return (
    <>
      <div style={{
        position: 'absolute',
        left: px - sz / 2, top: py - sz / 2,
        width: sz, height: sz, borderRadius: '50%',
        background: planet.hasRings
          ? `radial-gradient(circle at 35% 35%, ${planet.color}, ${planet.color}99)`
          : planet.color,
        boxShadow: `0 0 ${glow}px ${planet.color}${bright ? 'AA' : '40'}`,
        opacity: opacity * enter,
        transform: `scale(${enter})`,
      }} />
      {planet.hasRings && (
        <div style={{
          position: 'absolute',
          left: px - sz * 1.15, top: py - sz * 0.25,
          width: sz * 2.3, height: sz * 0.5,
          borderRadius: '50%',
          border: `2px solid ${planet.color}77`,
          background: `linear-gradient(transparent 40%, ${planet.color}22 50%, transparent 60%)`,
          opacity: opacity * enter * 0.7,
          transform: `scale(${enter})`,
        }} />
      )}
      {bright && enter > 0.5 && (
        <div style={{
          position: 'absolute',
          left: px - 30, top: py - sz / 2 - 22,
          width: 60, textAlign: 'center' as const,
          fontSize: 14, fontWeight: 700,
          color: planet.color, opacity: opacity * enter,
          textShadow: `0 0 6px ${planet.color}88`,
        }}>
          {planet.name}
        </div>
      )}
    </>
  );
};

// ===== Data Cell =====
const DataCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 6 }}>
    <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' as const }}>{label}</div>
    <div style={{
      fontSize: 22, color: 'rgba(255,255,255,0.9)', fontWeight: 600,
      whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis',
    }}>
      {value}
    </div>
  </div>
);

// ===== Planet Info Card =====
const InfoCard: React.FC<{
  planet: PlanetData;
  idx: number;
  frame: number;
  fps: number;
  phaseStart: number;
  opacity: number;
}> = ({ planet, idx, frame, fps, phaseStart, opacity }) => {
  const delay = phaseStart + idx * 25;
  const prog = spring({
    frame: frame - delay, fps,
    config: { damping: 14, stiffness: 180 },
  });
  if (frame < delay) return null;

  const slideX = interpolate(prog, [0, 1], [50, 0]);
  const y = 148 + idx * 225;

  return (
    <div style={{
      position: 'absolute', left: CARD_LEFT, top: y, width: CARD_W,
      padding: '14px 20px',
      background: 'rgba(255,255,255,0.06)',
      backdropFilter: 'blur(10px)',
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.08)',
      borderLeft: `3px solid ${planet.color}`,
      opacity: opacity * prog,
      transform: `translateX(${slideX}px)`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 14, height: 14, borderRadius: '50%',
          background: planet.color,
          boxShadow: `0 0 8px ${planet.color}`,
        }} />
        <span style={{ fontSize: 26, fontWeight: 700, color: '#fff' }}>{planet.name}</span>
        <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.35)' }}>{planet.nameEn}</span>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 6 }}>
        <DataCell label="直径" value={`${planet.diameter.toLocaleString()} km`} />
        <DataCell label="距日" value={`${planet.distanceAU} AU`} />
        <DataCell label="公转周期" value={`${planet.orbitalDays.toLocaleString()} 天`} />
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
        <DataCell label="已知卫星" value={`${planet.moons} 颗`} />
        <DataCell label="表面均温" value={planet.avgTemp} />
        <DataCell label="质量" value={planet.mass} />
      </div>

      <div style={{
        fontSize: 20, color: `${planet.color}BB`,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        paddingTop: 8, lineHeight: 1.4,
      }}>
        💡 {planet.funFact}
      </div>
    </div>
  );
};

// ===== Phase Label =====
const PhaseTag: React.FC<{
  title: string; sub: string;
  frame: number; fps: number; start: number; opacity: number;
}> = ({ title, sub, frame, fps, start, opacity }) => {
  const p = spring({ frame: frame - start, fps, config: { damping: 12, stiffness: 200 } });
  if (frame < start) return null;
  return (
    <div style={{
      position: 'absolute', top: 55, right: 70,
      textAlign: 'right' as const,
      opacity: opacity * p,
      transform: `translateY(${interpolate(p, [0, 1], [-15, 0])}px)`,
    }}>
      <div style={{ fontSize: 22, fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>{title}</div>
      <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>{sub}</div>
    </div>
  );
};

// ===== Size Comparison Chart =====
const SizeChart: React.FC<{
  frame: number; fps: number; start: number; opacity: number;
}> = ({ frame, fps, start, opacity }) => {
  const maxD = 142984;
  const barMax = 580;

  const summaryStats = [
    { label: '行星总数', val: '8', unit: '颗', c: '#FFD700' },
    { label: '已知卫星', val: '288', unit: '颗+', c: '#4A90E2' },
    { label: '最远距离', val: '45亿', unit: 'km', c: '#4169E1' },
    { label: '太阳系年龄', val: '~46亿', unit: '年', c: '#FF8C00' },
  ];

  return (
    <div style={{ position: 'absolute', left: CARD_LEFT, top: 200, width: CARD_W, opacity }}>
      <div style={{
        fontSize: 32, color: 'rgba(255,255,255,0.6)', fontWeight: 600, marginBottom: 20,
      }}>
        🪐 八大行星直径对比
      </div>
      {planets.map((p, i) => {
        const d = start + i * 6;
        const bp = spring({ frame: frame - d, fps, config: { damping: 14, stiffness: 160 } });
        if (frame < d) return null;
        const w = (p.diameter / maxD) * barMax;
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, opacity: bp,
          }}>
            <div style={{
              width: 75, fontSize: 24, color: 'rgba(255,255,255,0.5)', textAlign: 'right' as const,
            }}>
              {p.name}
            </div>
            <div style={{
              height: 24, width: w * bp, borderRadius: 11,
              background: `linear-gradient(90deg, ${p.color}, ${p.color}77)`,
            }} />
            <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>
              {p.diameter.toLocaleString()} km
            </div>
          </div>
        );
      })}

      <div style={{
        display: 'flex', gap: 28, marginTop: 32,
        padding: '18px 24px',
        background: 'rgba(255,255,255,0.04)',
        borderRadius: 14,
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        {summaryStats.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' as const }}>
            <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
              {s.label}
            </div>
            <span style={{ fontSize: 34, fontWeight: 700, color: s.c }}>{s.val}</span>
            <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>{s.unit}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Main Component =====
export const SolarSystemAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bgIn = spring({ frame, fps, config: { damping: 12, stiffness: 200 } });

  const titleP = spring({ frame: frame - 5, fps, config: { damping: 12, stiffness: 200 } });
  const titleY = interpolate(titleP, [0, 1], [-35, 0]);

  // Phase opacities: inner cards → outer cards → size comparison
  const innerOp = fadeRange(frame, 70, 100, 270, 300);
  const outerOp = fadeRange(frame, 290, 320, 460, 490);
  const summaryOp = interpolate(frame, [480, 510], [0, 1], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });

  const isInner = frame >= 70 && frame <= 300;
  const isOuter = frame >= 290 && frame <= 490;

  return (
    <AbsoluteFill style={{
      background: 'radial-gradient(ellipse at 30% 40%, #1a1a2e 0%, #0d0d1a 50%, #050510 100%)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      overflow: 'hidden',
    }}>
      <StarField opacity={bgIn * 0.35} />

      {/* Title */}
      <div style={{
        position: 'absolute', top: 40, left: 55,
        opacity: titleP, transform: `translateY(${titleY}px)`,
      }}>
        <h1 style={{
          fontSize: 68, fontWeight: 800, margin: 0,
          background: 'linear-gradient(135deg, #FF8C00, #FFD700)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          太阳系探秘
        </h1>
        <p style={{ fontSize: 26, margin: '8px 0 0', color: 'rgba(255,255,255,0.45)' }}>
          八大行星 · 真实天文数据可视化
        </p>
      </div>

      {/* Orbit rings */}
      {planets.map((p, i) => (
        <OrbitRing
          key={i}
          radius={p.orbitRadius}
          opacity={bgIn * 0.5}
          bright={(isInner && i < 4) || (isOuter && i >= 4)}
        />
      ))}

      {/* Asteroid belt between Mars and Jupiter */}
      <AsteroidBelt frame={frame} opacity={bgIn * 0.35} />

      <Sun opacity={bgIn} />

      {/* All 8 planets */}
      {planets.map((p, i) => (
        <PlanetBody
          key={i}
          planet={p}
          frame={frame}
          fps={fps}
          opacity={bgIn}
          bright={(isInner && i < 4) || (isOuter && i >= 4)}
        />
      ))}

      {/* Phase labels */}
      {innerOp > 0 && (
        <PhaseTag
          title="内行星 Inner Planets"
          sub="水星 · 金星 · 地球 · 火星"
          frame={frame} fps={fps} start={75} opacity={innerOp}
        />
      )}
      {outerOp > 0 && (
        <PhaseTag
          title="外行星 Outer Planets"
          sub="木星 · 土星 · 天王星 · 海王星"
          frame={frame} fps={fps} start={295} opacity={outerOp}
        />
      )}

      {/* Inner planet info cards */}
      {innerOp > 0 && innerPlanets.map((p, i) => (
        <InfoCard
          key={p.nameEn}
          planet={p} idx={i} frame={frame} fps={fps}
          phaseStart={85} opacity={innerOp}
        />
      ))}

      {/* Outer planet info cards */}
      {outerOp > 0 && outerPlanets.map((p, i) => (
        <InfoCard
          key={p.nameEn}
          planet={p} idx={i} frame={frame} fps={fps}
          phaseStart={305} opacity={outerOp}
        />
      ))}

      {/* Size comparison chart */}
      {frame >= 475 && (
        <SizeChart frame={frame} fps={fps} start={480} opacity={summaryOp} />
      )}

      {/* Bottom attribution */}
      <div style={{
        position: 'absolute', bottom: 35, left: 55, right: 55,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        opacity: interpolate(frame, [90, 110], [0, 1], {
          extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
        }),
      }}>
        <p style={{ fontSize: 24, margin: 0, color: 'rgba(255,255,255,0.3)' }}>
          数据来源：NASA Planetary Fact Sheet · 轨道速度按真实比例
        </p>
        {/* <div style={{
          padding: '7px 18px',
          background: 'rgba(255,140,0,0.12)',
          border: '1px solid rgba(255,140,0,0.35)',
          borderRadius: 8,
        }}>
          <span style={{ fontSize: 18, color: '#FF8C00' }}>🌟 硬核科普</span>
        </div> */}
      </div>
    </AbsoluteFill>
  );
};
